import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  useTheme,
  alpha,
  Tooltip,
  Fade,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { valueChains } from '../../data/valueChainData';

const ValueChainTable = ({ valueChainId, valueChainData: propValueChainData, onNavigate, industryName, industryId, subIndustryId, subIndustries }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [hoveredCell, setHoveredCell] = useState(null);
  const [detailStage, setDetailStage] = useState(null);

  // Use API data if available, otherwise fall back to local data
  const valueChainData = propValueChainData || valueChains[valueChainId];

  // Check if valueChainId is a UUID (contains dashes)
  const isUUID = valueChainId && valueChainId.includes('-');
  const displayName = industryName || (isUUID ? 'Industry' : valueChainId.charAt(0).toUpperCase() + valueChainId.slice(1));

  // Handle cases where valueChainId doesn't exist or stages are missing
  if (!valueChainData || !valueChainData.stages || valueChainData.stages.length === 0) {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.7)
        }}
      >
        <Typography variant="h6" color="text.secondary" fontWeight="medium">
          No value chain data available
        </Typography>
      </Paper>
    );
  }
  
  // Function to split content by commas and create separate items
  const splitContent = (content) => {
    if (!content) return [];
    return content.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  // Function to find sub-industry by name (exact match first, then partial)
  const findSubIndustryByName = (itemName) => {
    if (!subIndustries || !itemName) return null;

    const normalizedItemName = itemName.toLowerCase().trim();

    // Try exact match first
    let match = subIndustries.find(sub => {
      const normalizedSubName = sub.name.toLowerCase().trim();
      return normalizedSubName === normalizedItemName;
    });

    // If no exact match, try partial match
    if (!match) {
      match = subIndustries.find(sub => {
        const normalizedSubName = sub.name.toLowerCase().trim();
        return normalizedSubName.includes(normalizedItemName) || normalizedItemName.includes(normalizedSubName);
      });
    }

    return match;
  };

  // Handle item click - navigate to sub-industry or sub-sub-industry if match found, otherwise show modal
  const handleItemClick = (itemName, stage, colIndex) => {
    // Only try to navigate if we have the required data
    if (industryId && subIndustries && subIndustries.length > 0) {
      const matchedItem = findSubIndustryByName(itemName);

      if (matchedItem) {
        // If we're on a sub-industry page (subIndustryId is set), navigate to sub-sub-industry
        if (subIndustryId) {
          navigate(`/industry/${industryId}/sub/${subIndustryId}/subsub/${matchedItem.id}`);
        } else {
          // Otherwise, navigate to sub-industry page
          navigate(`/industry/${industryId}/sub/${matchedItem.id}`);
        }
        return;
      }
    }

    // Fall back to showing modal with stage details
    handleStageClick(stage, colIndex);
  };

  // Get categories from all stages to create consistent rows
  const getAllCategories = () => {
    const categories = new Set();
    valueChainData.stages.forEach(stage => {
      if (stage.categories) {
        Object.keys(stage.categories).forEach(category => {
          categories.add(category);
        });
      }
    });
    return Array.from(categories);
  };

  const categories = getAllCategories();

  // Generate a color for the header based on position (consistent gradient left to right)
  const getHeaderColor = (index, totalStages) => {
    // Create a gradient progression from left to right
    const colors = [
      theme.palette.primary.main,      // First color
      theme.palette.primary.dark,      
      theme.palette.secondary.main,    // Middle color
      theme.palette.secondary.dark,    
      theme.palette.primary.dark       // Last color
    ];
    
    // Calculate position along the gradient (0 to 1)
    const position = index / (totalStages - 1);
    
    // For a small number of stages, just use distinct colors
    if (totalStages <= colors.length) {
      return colors[index % colors.length];
    }
    
    // For more stages, interpolate between colors
    const segmentCount = colors.length - 1;
    const segment = Math.min(Math.floor(position * segmentCount), segmentCount - 1);
    const segmentPosition = (position * segmentCount) - segment;
    
    // Simple linear interpolation between adjacent colors
    return colors[segment];
  };

  const getCellHighlightColor = (index, totalStages, isHeader = false) => {
    if (isHeader) {
      return getHeaderColor(index, totalStages);
    }
    // Return appropriate alpha version for cell highlight
    return alpha(getHeaderColor(index, totalStages), 0.05);
  };
  
  // Calculate fixed column width for consistent alignment
  const columnWidth = `${100 / valueChainData.stages.length}%`;
  // Calculate minimum width per column (in pixels) to prevent text overlap
  const minColumnWidth = "150px"; // Adjust this value based on your content

  // Handle clicking on a stage header
  const handleStageClick = (stage, index) => {
    setDetailStage({ ...stage, index });
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setDetailStage(null);
  };

  // Render the detail view for a stage
  const renderDetailView = () => {
    if (!detailStage) return null;

    const stageIndex = detailStage.index;
    const headerColor = getHeaderColor(stageIndex, valueChainData.stages.length);

    // Extract data from additionalData if it exists, otherwise use direct properties
    const stageData = {
      ...detailStage,
      ...(detailStage.additionalData || {})
    };

    return (
      <Modal
        open={!!detailStage}
        onClose={handleCloseDetail}
        aria-labelledby="stage-detail-title"
        BackdropProps={{ 
          style: { backgroundColor: alpha('#000', 0.75) }
        }}
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto',
            p: 0,
            borderRadius: 3,
            boxShadow: 24,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, 
                ${headerColor}, 
                ${alpha(headerColor, 0.85)})`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#fff',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <Box>
              <Typography
                id="stage-detail-title"
                variant="h5"
                component="h2"
                fontWeight="bold"
              >
                {stageData.name || stageData.title}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                Stage {stageIndex + 1} of {valueChainData.stages.length}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseDetail}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            {/* Summary */}
            {stageData.summary && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Summary
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.default, 0.7),
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography variant="body1">
                    {stageData.summary}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Top Import Categories */}
            {stageData.topImportCategories && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Top Import Categories
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        {stageData.topImportCategories.headers.map((header, idx) => (
                          <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stageData.topImportCategories.rows.map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.background.default, 0.5) } }}>
                          {row.map((cell, cellIdx) => (
                            <TableCell key={cellIdx}>{cell}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Dominant Suppliers */}
            {stageData.dominantSuppliers && stageData.dominantSuppliers.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Dominant Suppliers
                </Typography>
                <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                  {stageData.dominantSuppliers.map((supplier, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        backgroundColor: idx % 2 === 0 ? 'transparent' : alpha(theme.palette.background.default, 0.5),
                        borderBottom: idx < stageData.dominantSuppliers.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                      }}
                    >
                      <Typography variant="body2">
                        {supplier}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}

            {/* Critical Dependencies */}
            {stageData.criticalDependencies && stageData.criticalDependencies.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Critical Dependencies
                </Typography>
                <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                  {stageData.criticalDependencies.map((dependency, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        backgroundColor: idx % 2 === 0 ? 'transparent' : alpha(theme.palette.background.default, 0.5),
                        borderBottom: idx < stageData.criticalDependencies.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                      }}
                    >
                      <Typography variant="body2">
                        {dependency}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}

            {/* Import Reduction Plan */}
            {stageData.importReductionPlan && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Import Reduction Plan
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.05),
                    borderColor: alpha(theme.palette.success.main, 0.2)
                  }}
                >
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    {stageData.importReductionPlan.text}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    {stageData.importReductionPlan.points.map((point, idx) => (
                      <Typography key={idx} component="li" variant="body2" sx={{ mb: 1 }}>
                        {point}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Products & Services Needed */}
            {stageData.productsServicesNeeded && stageData.productsServicesNeeded.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Products & Services Needed
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {stageData.productsServicesNeeded.map((item, idx) => (
                    <Chip
                      key={idx}
                      label={item}
                      variant="outlined"
                      color="primary"
                      sx={{
                        px: 1,
                        borderRadius: 2,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Key Players */}
            {stageData.keyPlayers && stageData.keyPlayers.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Key Players
                </Typography>
                <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                  {stageData.keyPlayers.map((player, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        backgroundColor: idx % 2 === 0 ? 'transparent' : alpha(theme.palette.background.default, 0.5),
                        borderBottom: idx < stageData.keyPlayers.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                      }}
                    >
                      <Typography variant="body2" dangerouslySetInnerHTML={{ __html: player.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: ' + theme.palette.primary.main + '; text-decoration: none; font-weight: 500;">$1</a>') }} />
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}

            {/* Trends */}
            {stageData.trends && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Trends
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }}
                >
                  <Typography variant="body1">
                    {stageData.trends}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Challenges */}
            {stageData.challenges && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Challenges
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
                  }}
                >
                  <Typography variant="body1">
                    {stageData.challenges}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Risk Rating */}
            {stageData.riskRating && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Risk Rating
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: (() => {
                      if (stageData.riskRating.includes('High') || stageData.riskRating.includes('Critical')) {
                        return alpha(theme.palette.error.main, 0.05);
                      } else if (stageData.riskRating.includes('Medium')) {
                        return alpha(theme.palette.warning.main, 0.05);
                      }
                      return alpha(theme.palette.success.main, 0.05);
                    })(),
                    border: `1px solid ${(() => {
                      if (stageData.riskRating.includes('High') || stageData.riskRating.includes('Critical')) {
                        return alpha(theme.palette.error.main, 0.1);
                      } else if (stageData.riskRating.includes('Medium')) {
                        return alpha(theme.palette.warning.main, 0.1);
                      }
                      return alpha(theme.palette.success.main, 0.1);
                    })()}`
                  }}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {stageData.riskRating}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Policy Recommendations */}
            {stageData.policyRecommendations && stageData.policyRecommendations.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Policy Recommendations
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                  }}
                >
                  <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
                    {stageData.policyRecommendations.map((recommendation, idx) => (
                      <Typography key={idx} component="li" variant="body2" sx={{ mb: 1 }}>
                        {recommendation}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Navigation buttons */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleCloseDetail}
              >
                Back to Table
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {stageIndex > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => handleStageClick(valueChainData.stages[stageIndex - 1], stageIndex - 1)}
                  >
                    Previous Stage
                  </Button>
                )}
                {stageIndex < valueChainData.stages.length - 1 && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleStageClick(valueChainData.stages[stageIndex + 1], stageIndex + 1)}
                    sx={{ backgroundColor: getHeaderColor(stageIndex + 1, valueChainData.stages.length) }}
                  >
                    Next Stage
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Modal>
    );
  };
  
  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      {/* Container with horizontal scroll */}
      <Box 
        sx={{ 
          position: 'relative',
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Headers - Fixed position */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            overflow: 'hidden'
          }}
        >
          {valueChainData.stages.map((stage, index) => (
            <Box
              key={index}
              sx={{
                width: columnWidth,
                minWidth: minColumnWidth,
                p: 2.5,
                background: `linear-gradient(135deg, 
                  ${getCellHighlightColor(index, valueChainData.stages.length, true)}, 
                  ${alpha(getCellHighlightColor(index, valueChainData.stages.length, true), 0.85)})`,
                borderRight: index < valueChainData.stages.length - 1 ? `1px solid ${alpha('#fff', 0.1)}` : 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                "&:hover": {
                  boxShadow: `0 4px 12px ${alpha(getCellHighlightColor(index, valueChainData.stages.length, true), 0.4)}`,
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => handleStageClick(stage, index)}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#fff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  maxWidth: '100%',
                  padding: '0 4px',
                  // Allow text to wrap
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}
              >
                {stage.name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Scrollable Content Area */}
        <Box 
          sx={{ 
            overflowX: 'auto',
            maxHeight: '600px',
            overflowY: 'auto',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.4),
              }
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.grey[200], 0.4),
              borderRadius: '4px',
            },
          }}
        >
          <Paper 
            elevation={0}
            sx={{
              minWidth: valueChainData.stages.length > 5 ? '1200px' : '100%', // Force minimum width for many columns
              borderRadius: 3,
              overflow: 'hidden',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}
          >
            {/* Rows for each category */}
            {categories.map((category, rowIndex) => (
              <React.Fragment key={rowIndex}>
                {/* Category header */}
                <Box
                  sx={{
                    p: 1.5,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <CategoryIcon 
                    fontSize="small" 
                    sx={{ color: theme.palette.text.secondary, opacity: 0.7 }} 
                  />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      color: theme.palette.text.primary
                    }}
                  >
                    {category}
                  </Typography>
                </Box>

                {/* Dynamic rows based on category content */}
                {(() => {
                  // Find maximum number of items in this category across all stages
                  let maxItems = 0;
                  valueChainData.stages.forEach(stage => {
                    if (stage.categories && stage.categories[category]) {
                      const items = splitContent(stage.categories[category]);
                      maxItems = Math.max(maxItems, items.length);
                    }
                  });
                  
                  // Create rows for each item
                  const rows = [];
                  for (let i = 0; i < maxItems; i++) {
                    rows.push(
                      <Box
                        key={`${category}-row-${i}`}
                        sx={{
                          display: 'flex',
                          borderBottom: i < maxItems - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                          backgroundColor: i % 2 === 0 ? 'background.paper' : alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        {valueChainData.stages.map((stage, colIndex) => {
                          const items = stage.categories && stage.categories[category] 
                            ? splitContent(stage.categories[category]) 
                            : [];
                          
                          const cellId = `${category}-${i}-${colIndex}`;
                          const isHovered = hoveredCell === cellId;
                          
                          return (
                            <Box
                              key={colIndex}
                              sx={{
                                width: columnWidth,
                                minWidth: minColumnWidth,
                                p: 2,
                                borderRight: colIndex < valueChainData.stages.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                backgroundColor: isHovered ? getCellHighlightColor(colIndex, valueChainData.stages.length) : 'transparent',
                                '&:after': isHovered ? {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: '10%',
                                  width: '80%',
                                  height: 2,
                                  backgroundColor: getCellHighlightColor(colIndex, valueChainData.stages.length, true)
                                } : {}
                              }}
                              onMouseEnter={() => items[i] && setHoveredCell(cellId)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {items[i] ? (() => {
                                const hasSubIndustry = findSubIndustryByName(items[i]);
                                return (
                                  <Tooltip
                                    title={hasSubIndustry ? `Click to explore ${items[i]}` : "Click to view details"}
                                    placement="top"
                                    TransitionComponent={Fade}
                                    TransitionProps={{ timeout: 600 }}
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          '& .arrow-icon': {
                                            opacity: 1,
                                            transform: 'translateX(2px)'
                                          }
                                        }
                                      }}
                                      onClick={() => handleItemClick(items[i], stage, colIndex)}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isHovered ? theme.palette.primary.main : theme.palette.text.primary,
                                          fontWeight: isHovered ? 500 : 400
                                        }}
                                      >
                                        {items[i]}
                                      </Typography>
                                      {hasSubIndustry && (
                                        <CheckCircleIcon
                                          sx={{
                                            fontSize: '0.875rem',
                                            color: theme.palette.success.main,
                                            opacity: 0.7
                                          }}
                                        />
                                      )}
                                      <ArrowForwardIcon
                                        className="arrow-icon"
                                        fontSize="small"
                                        sx={{
                                          color: theme.palette.primary.main,
                                          opacity: 0,
                                          transition: 'all 0.2s ease',
                                          fontSize: '1rem'
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                );
                              })() : (
                                <Box sx={{ minHeight: '1.5rem' }}></Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                  return rows;
                })()}
              </React.Fragment>
            ))}
            
            {/* For uncategorized content */}
            {(() => {
              // Check if any stage has uncategorized content
              const hasUncategorizedContent = valueChainData.stages.some(stage =>
                stage.content && splitContent(stage.content).length > 0
              );

              if (!hasUncategorizedContent) return null;

              // Check if there are any regular categories (hide header if only uncategorized content exists)
              const hasCategories = categories.length > 0;

              return (
                <React.Fragment>
                  {/* Uncategorized header - only show if there are also regular categories */}
                  {hasCategories && (
                  <Box
                    sx={{
                      p: 1.5,
                      px: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <CategoryIcon
                      fontSize="small"
                      sx={{ color: theme.palette.text.secondary, opacity: 0.7 }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary
                      }}
                    >
                      Uncategorized
                    </Typography>
                  </Box>
                  )}

                  {(() => {
                    // Find maximum number of uncategorized items across all stages
                    let maxItems = 0;
                    valueChainData.stages.forEach(stage => {
                      if (stage.content) {
                        const items = splitContent(stage.content);
                        maxItems = Math.max(maxItems, items.length);
                      }
                    });
                    
                    // Create rows for each uncategorized item
                    const rows = [];
                    for (let i = 0; i < maxItems; i++) {
                      rows.push(
                        <Box
                          key={`uncategorized-row-${i}`}
                          sx={{
                            display: 'flex',
                            borderBottom: i < maxItems - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                            backgroundColor: i % 2 === 0 ? 'background.paper' : alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          {valueChainData.stages.map((stage, colIndex) => {
                            const items = stage.content ? splitContent(stage.content) : [];
                            
                            const cellId = `uncategorized-${i}-${colIndex}`;
                            const isHovered = hoveredCell === cellId;
                            
                            return (
                              <Box
                                key={colIndex}
                                sx={{
                                  width: columnWidth,
                                  minWidth: minColumnWidth,
                                  p: 2,
                                  borderRight: colIndex < valueChainData.stages.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                  transition: 'all 0.2s ease',
                                  position: 'relative',
                                  backgroundColor: isHovered ? getCellHighlightColor(colIndex, valueChainData.stages.length) : 'transparent',
                                  '&:after': isHovered ? {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: '10%',
                                    width: '80%',
                                    height: 2,
                                    backgroundColor: getCellHighlightColor(colIndex, valueChainData.stages.length, true)
                                  } : {}
                                }}
                                onMouseEnter={() => items[i] && setHoveredCell(cellId)}
                                onMouseLeave={() => setHoveredCell(null)}
                              >
                                {items[i] ? (() => {
                                  const hasSubIndustry = findSubIndustryByName(items[i]);
                                  return (
                                    <Tooltip
                                      title={hasSubIndustry ? `Click to explore ${items[i]}` : "Click to view details"}
                                      placement="top"
                                      TransitionComponent={Fade}
                                      TransitionProps={{ timeout: 600 }}
                                      arrow
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          cursor: 'pointer',
                                          '&:hover': {
                                            '& .arrow-icon': {
                                              opacity: 1,
                                              transform: 'translateX(2px)'
                                            }
                                          }
                                        }}
                                        onClick={() => handleItemClick(items[i], stage, colIndex)}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: isHovered ? theme.palette.primary.main : theme.palette.text.primary,
                                            fontWeight: isHovered ? 500 : 400
                                          }}
                                        >
                                          {items[i]}
                                        </Typography>
                                        {hasSubIndustry && (
                                          <CheckCircleIcon
                                            sx={{
                                              fontSize: '0.875rem',
                                              color: theme.palette.success.main,
                                              opacity: 0.7
                                            }}
                                          />
                                        )}
                                        <ArrowForwardIcon
                                          className="arrow-icon"
                                          fontSize="small"
                                          sx={{
                                            color: theme.palette.primary.main,
                                            opacity: 0,
                                            transition: 'all 0.2s ease',
                                            fontSize: '1rem'
                                          }}
                                        />
                                      </Box>
                                    </Tooltip>
                                  );
                                })() : (
                                  <Box sx={{ minHeight: '1.5rem' }}></Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    }
                    return rows;
                  })()}
                </React.Fragment>
              );
            })()}
          </Paper>
        </Box>
      </Box>
      
      {/* Scroll indicator - appears when content is scrollable */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 2,
          mb: 4,
          color: theme.palette.text.secondary,
          fontSize: '0.75rem',
          opacity: 0.7
        }}
      >
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ 
            display: 'inline-block', 
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.6 }
            }
          }}>⟷</Box>
          Scroll horizontally to view all stages
        </Typography>
      </Box>
      
      {/* Additional content section with improved styling */}
      {valueChainData.additionalContent && valueChainData.additionalContent.length > 0 && (
        <Paper
          elevation={2}
          sx={{
            mt: 4,
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }}
          />
          
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 3,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                width: '40%',
                height: 3,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.main,
                bottom: -8,
                left: '30%'
              }
            }}
          >
            {displayName} Content
          </Typography>
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              gap: 1.5,
              mt: 4
            }}
          >
            {valueChainData.additionalContent.map((content, index) => (
              <Chip
                key={index}
                label={content}
                clickable
                color={index % 2 === 0 ? "primary" : "secondary"}
                variant="outlined"
                sx={{
                  px: 1,
                  py: 2.5,
                  borderRadius: 6,
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: index % 2 === 0 
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.secondary.main, 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.07)'
                  }
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Render detail modal when a stage is selected */}
      {renderDetailView()}
    </Box>
  );
};

export default ValueChainTable;